import { BulkFilingCreate, FilingFrequency, FilingTypeEnum } from '@/types/filings';
import { CountryCode } from '@/types/location';
import { isCountryLevelRegistration } from '@/utils/filters';
import { endOfMonth, format, subMonths } from 'date-fns';
import { z } from 'zod';

/** The API validates filing dates against UTC today, so anchor to UTC to avoid a day of skew. */
const utcToday = (from: Date = new Date()): Date => new Date(`${from.toISOString().slice(0, 10)}T00:00:00`);

/**
 * Latest end date allowed for bulk backfill. Period generation keeps whole natural
 * periods, so a range ending mid-month can still pull in a period that ends inside
 * the last-month window build_filing treats as REGULAR. Capping at the end of the
 * month two months back keeps the requested range clear of that window.
 */
export const maxBulkBackfillEndDate = (from: Date = new Date()): Date => endOfMonth(subMonths(utcToday(from), 2));

export const maxBulkBackfillEndDateIso = (from: Date = new Date()): string =>
  format(maxBulkBackfillEndDate(from), 'yyyy-MM-dd');

export const initialPeriod = {
  start_date: '',
  end_date: '',
  filing_category: '',
} as const satisfies { start_date: string; end_date: string; filing_category: string };

const periodSchema = z
  .object({
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().min(1, 'End date is required'),
    filing_category: z
      .string()
      .min(1, 'Type is required')
      .refine((value): value is FilingTypeEnum => Object.values(FilingTypeEnum).includes(value as FilingTypeEnum), {
        message: 'Type is required',
      }),
  })
  .refine(data => !data.start_date || !data.end_date || new Date(data.end_date) >= new Date(data.start_date), {
    message: "End date can't be before start date",
    path: ['end_date'],
  });

const filingFormSchema = z
  .object({
    id: z.string(),
    registration_id: z.string().min(1, 'Jurisdiction is required'),
    is_manual: z.boolean(),
    state_name: z.string(),
    state_code: z.string(),
    country_code: z.string().min(1, 'Country Code is required'),
    periods: z.array(periodSchema).min(1, 'At least one period is required'),
  })
  .superRefine((data, ctx) => {
    if (isCountryLevelRegistration(data.country_code)) {
      return;
    }
    if (!data.state_name) {
      ctx.addIssue({ code: 'custom', message: 'Jurisdiction is required', path: ['state_name'] });
    }
    if (!data.state_code) {
      ctx.addIssue({ code: 'custom', message: 'Jurisdiction is required', path: ['state_code'] });
    }
  });

export const newFilingModalSchema = z.object({
  forms: z.array(filingFormSchema).min(1, 'At least one form is required'),
});

export type FilingFormPeriodInput = z.input<typeof periodSchema>;
export type FilingFormValues = z.output<typeof filingFormSchema>;
export type NewFilingModalFormData = z.input<typeof newFilingModalSchema>;
export type NewFilingModalFormOutput = z.output<typeof newFilingModalSchema>;

export const createInitialFilingForm = (): NewFilingModalFormData['forms'][number] => ({
  id: crypto.randomUUID(),
  registration_id: '',
  is_manual: true,
  state_name: '',
  state_code: '',
  country_code: CountryCode.US,
  periods: [
    {
      start_date: initialPeriod.start_date,
      end_date: initialPeriod.end_date,
      filing_category: initialPeriod.filing_category,
    },
  ],
});

const decimalAmountPattern = /^\d*(\.\d{0,2})?$/;
const signedDecimalAmountPattern = /^-?\d*(\.\d{0,2})?$/;

const optionalAmountString = (pattern: RegExp, message: string) =>
  z.string().refine(value => value === '' || pattern.test(value), { message });

export const filingConfirmationFormSchema = z.object({
  filingConfirmationNumber: z.string().trim().min(1, 'Filing confirmation number is required'),
  paymentConfirmationNumber: z.string().trim().min(1, 'Payment confirmation number is required'),
  fees: optionalAmountString(decimalAmountPattern, 'Must be a valid number with up to 2 decimal places'),
  penalties: optionalAmountString(decimalAmountPattern, 'Must be a valid number with up to 2 decimal places'),
  adjustments: optionalAmountString(
    signedDecimalAmountPattern,
    'Must be a valid number (can be negative) with up to 2 decimal places'
  ),
  discounts: optionalAmountString(decimalAmountPattern, 'Must be a valid number with up to 2 decimal places'),
  filingAttachment: z.custom<File | null>().nullable(),
  paymentAttachment: z.custom<File | null>().nullable(),
});

export type FilingConfirmationFormData = z.infer<typeof filingConfirmationFormSchema>;

export const createFilingConfirmationDefaultValues = (filing: {
  return_confirmation_id?: string | null;
  payment_confirmation_id?: string | null;
  amount_adjusted?: number | string | null;
  amount_discounts?: number | string | null;
  amount_fees?: number | string | null;
  amount_penalties?: number | string | null;
}): FilingConfirmationFormData => ({
  filingConfirmationNumber: filing.return_confirmation_id || '',
  paymentConfirmationNumber: filing.payment_confirmation_id || '',
  adjustments: String(filing.amount_adjusted ?? ''),
  discounts: String(filing.amount_discounts ?? ''),
  fees: String(filing.amount_fees ?? ''),
  penalties: String(filing.amount_penalties ?? ''),
  filingAttachment: null,
  paymentAttachment: null,
});

export const buildBulkFilingPayload = (forms: FilingFormValues[]): BulkFilingCreate => ({
  filings: forms.map(form => ({
    registration_id: form.registration_id,
    country_code: form.country_code,
    state_code: form.state_code,
    periods: form.periods.map(period => ({
      start_date: period.start_date,
      end_date: period.end_date,
      filing_category: period.filing_category,
    })),
  })),
});

const bulkBackfillFrequencyValues = Object.values(FilingFrequency).filter(
  value => value !== FilingFrequency.SEMI_MONTHLY
) as [string, ...string[]];

export const bulkBackfillFormSchema = z
  .object({
    country_code: z.string().min(1, 'Country is required'),
    registration_id: z.string().min(1, 'Jurisdiction is required'),
    start_date: z.string().min(1, 'Start date is required'),
    end_date: z.string().min(1, 'End date is required'),
    filing_frequency: z.enum(bulkBackfillFrequencyValues, {
      message: 'Filing frequency is required',
    }),
  })
  .refine(data => !data.start_date || !data.end_date || data.end_date >= data.start_date, {
    message: 'End date must be on or after start date',
    path: ['end_date'],
  })
  .refine(
    data => {
      if (!data.end_date) return true;
      return data.end_date <= maxBulkBackfillEndDateIso();
    },
    {
      message:
        'End date must be on or before the end of the month two months back. Later periods are created as regular filings automatically.',
      path: ['end_date'],
    }
  );

export type BulkBackfillFormData = z.infer<typeof bulkBackfillFormSchema>;

export const createInitialBulkBackfillForm = (): BulkBackfillFormData => ({
  country_code: '',
  registration_id: '',
  start_date: '',
  end_date: '',
  filing_frequency: FilingFrequency.MONTHLY,
});
